import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';
import { CV_TECHNOLOGY_SOURCES } from '../../../constants/cvTechnologySources';

export const SummaryContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: ${props => props.$bgColor || COLORS.BACKGROUND_LIGHT};
  border-radius: 8px;
  border: 1px solid ${props => props.$borderColor || '#e0e0e0'};
  min-width: 140px;

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${props => props.$iconBgColor || COLORS.PRIMARY};
    color: white;
    font-size: 14px;
  }

  .content {
    display: flex;
    flex-direction: column;
  }

  .count {
    font-size: 20px;
    font-weight: 600;
    color: ${props => props.$textColor || COLORS.PRIMARY};
    line-height: 1.2;
  }

  .label {
    font-size: 12px;
    color: ${COLORS.GRAY};
    font-weight: 500;
  }
`;

export const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 16px;
`;

export const ComparisonColumn = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BACKGROUND_LIGHT};
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

export const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: ${props => props.$bgColor || COLORS.PRIMARY};
  color: white;
  font-weight: 600;
  font-size: 14px;

  svg {
    font-size: 18px;
  }
`;

export const ColumnContent = styled.div`
  padding: 8px;
  max-height: 400px;
  overflow-y: auto;
`;

export const TechnologyItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: white;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .tech-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .tech-name {
    font-weight: 500;
    color: ${COLORS.PRIMARY};
    font-size: 14px;
  }

  .tech-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const LevelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => props.$bgColor || '#e0e0e0'};
  color: ${props => props.$textColor || '#333'};
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.$status) {
      case 'matched':
        return '#dcfce7';
      case 'mismatch':
        return '#fef3c7';
      case 'only-radar':
        return '#fee2e2';
      case 'only-cv':
        return '#dbeafe';
      default:
        return '#e0e0e0';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'matched':
        return '#166534';
      case 'mismatch':
        return '#92400e';
      case 'only-radar':
        return '#991b1b';
      case 'only-cv':
        return '#1e40af';
      default:
        return '#333';
    }
  }};

  svg {
    font-size: 12px;
  }
`;

export const SourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background-color: ${props => props.$source === CV_TECHNOLOGY_SOURCES.GENERAL_SKILL ? '#e0f2fe' : '#fce7f3'};
  color: ${props => props.$source === CV_TECHNOLOGY_SOURCES.GENERAL_SKILL ? '#0369a1' : '#be185d'};

  svg {
    font-size: 10px;
  }
`;

export const DifferenceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background-color: ${props => props.$positive ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.$positive ? '#166534' : '#991b1b'};

  svg {
    font-size: 10px;
  }
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${props => props.disabled ? '#f5f5f5' : 'white'};
  color: ${props => props.disabled ? COLORS.GRAY : COLORS.PRIMARY};
  border: 1px solid ${props => props.disabled ? '#e0e0e0' : COLORS.PRIMARY};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  svg {
    font-size: 12px;
    transition: transform 0.2s ease;
  }

  &:not(:disabled):hover {
    background-color: ${COLORS.PRIMARY};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:not(:disabled):hover svg {
    transform: scale(1.1);
  }

  &:not(:disabled):active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
  color: ${COLORS.GRAY};

  svg {
    font-size: 32px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  .title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
  }

  .description {
    font-size: 12px;
    opacity: 0.7;
  }
`;

export const ComparisonSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: ${props => props.$bgColor || COLORS.BACKGROUND};
  border-radius: 8px 8px 0 0;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.$textColor || COLORS.PRIMARY};

  svg {
    font-size: 16px;
  }

  .count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 8px;
    border-radius: 12px;
    background-color: ${props => props.$countBgColor || COLORS.PRIMARY};
    color: white;
    font-size: 12px;
    margin-left: auto;
  }
`;

export const SectionContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 0 0 8px 8px;
`;

export const TechCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: ${COLORS.BACKGROUND_LIGHT};
  border-radius: 8px;
  border: 1px solid #e8e8e8;

  .left {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .tech-name {
    font-weight: 500;
    color: ${COLORS.PRIMARY};
    font-size: 14px;
  }

  .tech-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;
